import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { BookmarkSimple, Play, FloppyDisk } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Bookmarks() {
    const [items, setItems] = useState([]);
    const nav = useNavigate();

    useEffect(() => {
        api.get("/bookmarks").then((r) => setItems(r.data.items || []));
    }, []);

    const launchAll = () => {
        if (items.length === 0) return;
        nav("/practice", { state: { questions: items, label: `${items.length} bookmarked questions` } });
    };

    const saveAsSet = async () => {
        if (items.length === 0) return;
        const name = prompt(`Save all ${items.length} bookmarks as a study set — name it:`);
        if (!name) return;
        try {
            await api.post("/sets", { name, question_ids: items.map((q) => q.id) });
            toast.success(`Saved "${name}"`);
        } catch {
            toast.error("Failed to save set");
        }
    };

    return (
        <div className="mx-auto max-w-4xl px-6 py-12">
            <div className="mb-6 flex flex-col items-start justify-between gap-3 md:flex-row md:items-center">
                <div>
                    <div className="overline text-primary">Bookmarks</div>
                    <h1 className="font-display text-4xl font-semibold tracking-tight">Saved for review</h1>
                </div>
                {items.length > 0 && (
                    <div className="flex gap-2">
                        <Button onClick={launchAll} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="bookmarks-launch">
                            <Play size={14} className="mr-1" /> Practice all
                        </Button>
                        <Button onClick={saveAsSet} variant="outline" data-testid="bookmarks-save-set">
                            <FloppyDisk size={14} className="mr-1" /> Save as set
                        </Button>
                    </div>
                )}
            </div>
            <div className="mt-4 space-y-6">
                {items.length === 0 && <p className="text-muted-foreground">No bookmarks yet. Flag questions during practice to save them here.</p>}
                {items.map((q) => (
                    <div key={q.id} className="border border-border bg-card p-6" data-testid={`bookmark-${q.id}`}>
                        <div className="flex items-start justify-between gap-3">
                            <div className="overline text-primary">{q.domain}</div>
                            <BookmarkSimple weight="fill" className="text-primary" />
                        </div>
                        <p className="mt-3">{q.question}</p>
                        <ul className="mt-4 space-y-2 text-sm">
                            {q.options.map((o, i) => (
                                <li key={i} className={`border p-3 ${i === q.correct_index ? "border-emerald-500 bg-emerald-50/60 dark:bg-emerald-950/20" : "border-border"}`}>
                                    <span className="font-mono text-xs text-primary">{String.fromCharCode(65 + i)}</span> · {o}
                                </li>
                            ))}
                        </ul>
                        {q.explanation && <p className="mt-4 border-l-2 border-primary bg-secondary/40 p-3 text-sm text-muted-foreground">{q.explanation}</p>}
                    </div>
                ))}
            </div>
        </div>
    );
}
